import React, {
  useRef,
  useState,
} from "react";

import axios from "axios";

import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";

import {
  useNavigate,
  useLocation,
} from "react-router-dom";

import { useAppContext } from "../context/AppContext";

export default function SubscriptionPayment() {

  const navigate = useNavigate();

  const location = useLocation();

  const fileInputRef =
    useRef(null);

  // =====================================
  // APP CONTEXT
  // =====================================

  const {
    clientId: contextClientId,
    user,
    company,
  } = useAppContext();

  // =====================================
  // INVOICE
  // =====================================

  const invoice =
    location.state?.invoice || null;

  // =====================================
  // PAYMENT STATE
  // =====================================

  const [paymentMode, setPaymentMode] =
    useState("UPI");

  const [referenceNo, setReferenceNo] =
    useState("");

  const [selectedFile, setSelectedFile] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  // =====================================
  // CLIENT ID
  //
  // Priority:
  //
  // 1. Invoice client_id
  // 2. AppContext clientId
  // 3. Logged-in user client_id
  // 4. Company client_id
  // 5. Old localStorage values
  // =====================================

  const clientId =
    invoice?.client_id ||
    contextClientId ||
    user?.client_id ||
    company?.client_id ||
    localStorage.getItem("clientId") ||
    localStorage.getItem("trialClientId") ||
    null;

  // =====================================
  // INVOICE DETAILS
  // =====================================

  const invoiceId =
    invoice?.invoice_id;

  const invoiceNo =
    invoice?.invoice_no || "-";

  const amount =
    Number(
      invoice?.balance_amount ||
      invoice?.amount_before_due ||
      0
    );

  // =====================================
  // SUBMIT PAYMENT
  // =====================================

  const handleSubmitPayment =
    async () => {

      try {

        // =====================================
        // CLIENT CHECK
        // =====================================

        if (!clientId) {

          console.error(
            "❌ Client ID not found",
            {
              invoiceClientId:
                invoice?.client_id,

              contextClientId,

              userClientId:
                user?.client_id,

              companyClientId:
                company?.client_id,

              localStorageClientId:
                localStorage.getItem(
                  "clientId"
                ),

              trialClientId:
                localStorage.getItem(
                  "trialClientId"
                ),
            }
          );

          alert(
            "Client information not found. Please login again."
          );

          return;
        }

        // =====================================
        // INVOICE CHECK
        // =====================================

        if (!invoiceId) {

          alert(
            "Invoice information not found."
          );

          return;
        }

        // =====================================
        // REFERENCE CHECK
        // =====================================

        if (
          !referenceNo.trim()
        ) {

          alert(
            "Please enter Reference / UTR Number"
          );

          return;
        }

        // =====================================
        // SCREENSHOT CHECK
        // =====================================

        if (!selectedFile) {

          alert(
            "Please upload payment screenshot"
          );

          return;
        }

        // =====================================
        // DEBUG
        // =====================================

        console.log(
          "💳 Submitting Payment:",
          {
            clientId,
            invoiceId,
            invoiceNo,
            amount,
            paymentMode,
            referenceNo:
              referenceNo.trim(),
            company:
              company?.name,
          }
        );

        setLoading(true);

        // =====================================
        // FORM DATA
        // =====================================

        const formData =
          new FormData();

        formData.append(
          "invoice_id",
          invoiceId
        );

        formData.append(
          "client_id",
          Number(clientId)
        );

        formData.append(
          "payment_mode",
          paymentMode
        );

        formData.append(
          "reference_no",
          referenceNo.trim()
        );

        formData.append(
          "payment_amount",
          amount
        );

        formData.append(
          "screenshot",
          selectedFile
        );

        // =====================================
        // API
        // =====================================

        const response =
          await axios.post(
            "/api/admin/subscription-payments",
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        // =====================================
        // SUCCESS
        // =====================================

        alert(
          response.data?.message ||
          "Payment submitted successfully"
        );

        // =====================================
        // RETURN TO DASHBOARD
        // =====================================

        navigate(
          "/",
          {
            replace: true,

            state: {
              paymentSubmitted:
                true,
            },
          }
        );

      } catch (error) {

        console.error(
          "❌ Subscription Payment Error:",
          error
        );

        console.error(
          "❌ Server Response:",
          error?.response?.data
        );

        alert(
          error?.response?.data?.message ||
          "Failed to submit payment"
        );

      } finally {

        setLoading(false);
      }
    };

  // =====================================
  // RENDER
  // =====================================

  return (

    <Box
      sx={{
        minHeight:
          "calc(100vh - 20px)",

        overflowY:
          "auto",

        bgcolor:
          "#F4F7FE",

        p: {
          xs: 1,
          sm: 2,
          md: 3,
        },
      }}
    >

      <Paper
        elevation={4}
        sx={{
          maxWidth: 650,

          mx: "auto",

          p: {
            xs: 2,
            sm: 3,
          },

          borderRadius: 3,
        }}
      >

        {/* ================================= */}
        {/* HEADER */}
        {/* ================================= */}

        <Typography
          variant="h4"
          fontWeight="bold"
          sx={{
            color:
              "#2563EB",

            mb: 1,
          }}
        >
          Subscription Payment
        </Typography>

        <Typography
          color="text.secondary"
          sx={{
            mb: 2,
          }}
        >
          Submit your subscription payment
          for administrator verification.
        </Typography>

        <Divider
          sx={{
            mb: 3,
          }}
        />

        {/* ================================= */}
        {/* INVOICE */}
        {/* ================================= */}

        <Box
          sx={{
            bgcolor:
              "#EFF6FF",

            border:
              "1px solid #BFDBFE",

            borderRadius: 2,

            p: 2,

            mb: 2,
          }}
        >

          <Typography
            variant="h6"
            fontWeight="bold"
          >
            Invoice
          </Typography>

          {/* Invoice No */}

          <Box
            sx={{
              display:
                "flex",

              justifyContent:
                "space-between",

              mt: 1,
            }}
          >

            <Typography>
              Invoice No
            </Typography>

            <Typography
              fontWeight="bold"
            >
              {invoiceNo}
            </Typography>

          </Box>

          {/* Amount */}

          <Box
            sx={{
              display:
                "flex",

              justifyContent:
                "space-between",

              mt: 1,
            }}
          >

            <Typography>
              Amount Due
            </Typography>

            <Typography
              sx={{
                fontSize: 24,

                fontWeight:
                  "bold",

                color:
                  "#2563EB",
              }}
            >
              ₹
              {amount.toLocaleString(
                "en-IN",
                {
                  minimumFractionDigits:
                    2,
                }
              )}
            </Typography>

          </Box>

          {/* Due Date */}

          {invoice?.due_date && (

            <Box
              sx={{
                display:
                  "flex",

                justifyContent:
                  "space-between",

                mt: 1,
              }}
            >

              <Typography>
                Due Date
              </Typography>

              <Typography
                fontWeight="bold"
              >
                {new Date(
                  invoice.due_date
                ).toLocaleDateString(
                  "en-GB"
                )}
              </Typography>

            </Box>

          )}

        </Box>

        {/* ================================= */}
        {/* PAYMENT INFORMATION */}
        {/* ================================= */}

        <Box
          sx={{
            bgcolor:
              "#F8FAFC",

            borderRadius: 2,

            p: 2,

            mb: 2,
          }}
        >

          <Typography
            variant="h6"
            fontWeight="bold"
            color="#2563EB"
          >
            Make Payment
          </Typography>

          <Typography
            sx={{
              mt: 2,
            }}
          >
            UPI ID :
            <strong>
              {" "}
              wheelserp@okaxis
            </strong>
          </Typography>

          <Box
            sx={{
              mt: 2,

              height: 100,

              border:
                "1px dashed #CBD5E1",

              borderRadius: 2,

              display:
                "flex",

              alignItems:
                "center",

              justifyContent:
                "center",

              bgcolor:
                "white",
            }}
          >

            <Typography
              color="text.secondary"
            >
              QR CODE HERE
            </Typography>

          </Box>

          <Divider
            sx={{
              my: 2,
            }}
          />

          <Typography
            fontWeight="bold"
          >
            Bank Transfer
          </Typography>

          <Typography mt={1}>
            Account Name :
            WheelsERP Technologies
          </Typography>

          <Typography>
            Account No :
            1234567890
          </Typography>

          <Typography>
            IFSC :
            SBIN0001234
          </Typography>

        </Box>

        {/* ================================= */}
        {/* PAYMENT MODE */}
        {/* ================================= */}

        <FormControl
          fullWidth
          size="small"
          sx={{
            mb: 2,
          }}
        >

          <InputLabel>
            Payment Mode
          </InputLabel>

          <Select
            value={
              paymentMode
            }
            label="Payment Mode"
            onChange={(e) =>
              setPaymentMode(
                e.target.value
              )
            }
          >

            <MenuItem value="UPI">
              UPI
            </MenuItem>

            <MenuItem value="BANK">
              Bank Transfer
            </MenuItem>

          </Select>

        </FormControl>

        {/* ================================= */}
        {/* UTR */}
        {/* ================================= */}

        <TextField
          fullWidth
          size="small"
          label="Reference Number / UTR Number"
          value={referenceNo}
          onChange={(e) =>
            setReferenceNo(
              e.target.value
            )
          }
          sx={{
            mb: 2,
          }}
        />

        {/* ================================= */}
        {/* FILE INPUT */}
        {/* ================================= */}

        <input
          type="file"
          accept="image/*,.pdf"
          ref={fileInputRef}
          style={{
            display:
              "none",
          }}
          onChange={(e) => {

            const file =
              e.target.files?.[0];

            if (file) {
              setSelectedFile(file);
            }

          }}
        />

        <Button
          variant="outlined"
          fullWidth
          onClick={() =>
            fileInputRef.current?.click()
          }
          sx={{
            mb: 1,

            height: 45,
          }}
        >
          {selectedFile
            ? selectedFile.name
            : "CHOOSE PAYMENT SCREENSHOT"}
        </Button>

        {selectedFile && (

          <Typography
            fontSize={12}
            color="success.main"
            sx={{
              mb: 2,
            }}
          >
            Selected :
            {" "}
            {selectedFile.name}
          </Typography>

        )}

        {/* ================================= */}
        {/* SUBMIT */}
        {/* ================================= */}

        <Button
          variant="contained"
          fullWidth
          disabled={
            loading
          }
          onClick={
            handleSubmitPayment
          }
          sx={{
            height: 48,

            fontSize:
              "15px",

            fontWeight:
              "bold",

            backgroundColor:
              "#16A34A",

            "&:hover": {
              backgroundColor:
                "#15803D",
            },
          }}
        >

          {loading ? (

            <>
              <CircularProgress
                size={20}
                color="inherit"
                sx={{
                  mr: 1,
                }}
              />

              SUBMITTING...
            </>

          ) : (

            "SUBMIT PAYMENT"

          )}

        </Button>

        {/* ================================= */}
        {/* CANCEL */}
        {/* ================================= */}

        <Button
          variant="text"
          fullWidth
          sx={{
            mt: 1,
          }}
          onClick={() =>
            navigate("/")
          }
        >
          Cancel
        </Button>

      </Paper>

    </Box>
  );
}